import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import {EMPTY_ARRAY, TuiHandler} from '@taiga-ui/cdk';
import { TuiDialogContext } from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@tinkoff/ng-polymorpheus';
import { TreeNode, treeStructure } from './tree-structure';

export interface favouritesSettingsDialogOutput {
  columns: Array<string>;
  operationType: string;
}

export interface favouritesSettingsDialogInput {
  initColumns: Array<string>;
  allColumns: Array<{caption: string, dataField: string}>;
}

export interface ISearchItem {
  value: string;
  parents: string;
}

@Component({
  selector: 'iv-live-columns-chooser-tree',
  templateUrl: './columns-chooser-tree.component.html',
  styleUrls: ['./columns-chooser-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ColumnsChooserTreeComponent implements OnInit {
  public initColumns: Array<string> = [];
  public allColumns: Array<{ caption: string; dataField: string }> = [];
  public map = new Map<TreeNode, boolean>();
  public toggleMap = new Map<TreeNode, boolean>();
  public showFoundSettings = false;
  public searchValue = '';
  public searchItems: Array<ISearchItem> = [];
  readonly data: TreeNode = treeStructure;

  readonly handler: TuiHandler<TreeNode, readonly TreeNode[]> = (item) =>
    item.children || EMPTY_ARRAY

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<favouritesSettingsDialogOutput, favouritesSettingsDialogInput>,
  ) {

  }

  public ngOnInit() {
    this.initTreeCheckedNodes();
  }

  readonly getValue = (
    item: TreeNode,
    map: Map<TreeNode, boolean>
  ): boolean | null => {
    const flat = flatten(item);
    const result = !!map.get(flat[0]);

    for (const item of flat) {
      if (result !== !!map.get(item)) {
        return null;
      }
    }

    return result;
  }

  public onChecked(node: TreeNode, value: boolean): void {
    flatten(node).forEach((item) => this.map.set(item, value));
    this.map = new Map(this.map.entries());
  }

  public selectAll(): void {
    this.onChecked(this.data, true);
    this.collapseTree();
  }

  public unselectAll(): void {
    this.map = new Map<TreeNode, boolean>();
    this.collapseTree();
  }

  public saveColumns(): void {
    this.context.completeWith({
      columns: this.getColumns(),
      operationType: 'saveColumns',
    });
  }

  public saveAsDefault(): void {
    this.context.completeWith({
      columns: this.getColumns(),
      operationType: 'saveAsDefault',
    });
  }

  public searchSettings(text: string): void {
    if (text.length) {
      this.showFoundSettings = true;
      const newSearchItems: ISearchItem[] = [];
      const treeNodes = findSettinsByText(this.data, text);
      treeNodes.forEach((treeNode: TreeNode) => {
        newSearchItems.push({
          value: treeNode.text,
          parents: findParents(this.data, treeNode)
        });
      });
      this.searchItems = newSearchItems;
    } else {
      this.showFoundSettings = false;
    }
  }

  public isSettingChecked(caption: string): boolean {
    return this.getColumns()?.some( dataField => dataField === this.getDataFieldByCaption(caption));
  }

  public findNodeByText(node: TreeNode, text: string): TreeNode | null {
    if (node.text === text) return node;
    if (node.children) {
      let result = null;
      for (let i = 0; result === null && i < node.children.length; i++) {
        result = this.findNodeByText(node.children[i], text);
      }
      return result;
    }
    return null;
  }

  private getColumns(): Array<string> {
    const result = [];
    [...this.map.entries()].forEach((item) => {
      if (item[1]) result.push(this.getDataFieldByCaption(item[0].text));
    });
    return result;
  }

  private getCaptionByDataField(dataField: string): string | null {
    let caption = null;
    this.allColumns.forEach((column) => {
      if (column.dataField === dataField) {
        caption = column.caption;
      }
    });
    return caption;
  }

  private getDataFieldByCaption(caption: string): string {
    let dataField = null;
    this.allColumns.forEach((column) => {
      if (column.caption.trim() === caption) {
        dataField = column.dataField;
      }
    });
    return dataField;
  }

  private initTreeCheckedNodes(): void {
    this.initColumns = this.context.data.initColumns;
    this.allColumns = this.context.data.allColumns;
    this.initColumns.forEach((dataField: string) => {
      const caption = this.getCaptionByDataField(dataField)?.trim();
      if (caption) {
        const node = this.findNodeByText(this.data, caption);
        if (node) this.onChecked(node, true);
      }
    });
  }

  private collapseTree(): void {
    this.toggleMap = new Map<TreeNode, boolean>();
  }
}

function flatten(item: TreeNode): readonly TreeNode[] {
  return item.children
      ? item.children.map(flatten).reduce((arr, item) => [...arr, ...item], [])
      : [item];
}

function findSettinsByText(allSettingsTree: TreeNode, text: string): TreeNode[] {
  const result: TreeNode[] = [];
  flatten(allSettingsTree).forEach((node: TreeNode) => {
    if (node.text.toLowerCase().includes(text.toLowerCase())) result.push(node);
  });
  return result;
}

function findParents(tree, targetNode): string {
  let parents = '';

  function traverse(node, path = '') {
    const currentPath = path ? `${path} -> ${node.text}` : node.text;

    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        if (child === targetNode) {
          parents = currentPath;
        } else {
          traverse(child, currentPath);
        }
      });
    }
  }

  traverse(tree);

  return parents;
}

